import { useEffect, useMemo, useState } from 'react'

import { faBriefcase, faClinicMedical, faPeopleArrows } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Drawer, Popconfirm, Timeline, Tooltip } from 'antd'
import moment from 'moment'
import { useSelector } from 'react-redux'

import { selectUser } from '../../reducers/UserReducer'
import { getMaccsInternshipDocuments, getMaccsInternshipShifts } from '../../utils/api/maccsInternship'
import {
  downloadConvention,
  lockedMaccsInternshipDocuments
} from '../../utils/api/maccsInternshipDocument'
import { getContactPersonsSchoolSpeciality } from '../../utils/api/schoolSpeciality'
import { getContactPersonsSpecialityYear } from '../../utils/api/specialityYear'
import { InternshipStates } from '../../utils/constants'
import FilesManager from './FilesManager'
import InformationDisplay from './InformationDisplay'
import LoadingSkeleton from './LoadingSkeleton'
import InternshipValidationButton from './ShiftsManager/InternshipValidationButton'
import MaccsInternshipValidation from './ShiftsManager/MaccsInternshipValidation/MaccsInternshipValidation'
import { getTimezoneLessMoment } from '../../utils/momentjs'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { isObject } from '../../utils'

const MaccsInternshipInfo = ({ maccsInternship, onClose, onMaccsInternshipValidate }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [openValidationDrawer, setOpenValidationDrawer] = useState(false)
  const [maccsInternshipFilesManagerOpen, setMaccsInternshipFilesManagerOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingContactPersons, setLoadingContactPersons] = useState(true)
  const [contactPersons, setContactPersons] = useState([])
  const [shifts, setShifts] = useState([])
  const [documents, setDocuments] = useState([])

  useEffect(() => {
    if (maccsInternship !== null) {
      setLoading(true)
      loadContactPersons(maccsInternship)

      const promiseStack = []

      promiseStack.push(
        getMaccsInternshipShifts(maccsInternship, user)
          .then((data) => {
            setShifts(data)
          })
      )

      promiseStack.push(
        getMaccsInternshipDocuments(maccsInternship, user)
          .then((data) => {
            setDocuments(data)
          })
      )

      Promise.all(promiseStack).then(() => {
        setLoading(false)
      })
    }
  }, [maccsInternship?.id, user])

  const loadContactPersons = maccsInternship => {
    const { internshipYear, schoolSpeciality } = maccsInternship
    const promiseStack = []
    let contactPersonSchoolSpeciality = []
    let contactPersonSpecialityYear = []

    setLoadingContactPersons(true)

    if (schoolSpeciality) {
      promiseStack.push(
        getContactPersonsSchoolSpeciality(schoolSpeciality, user)
          .then(data => {
            contactPersonSchoolSpeciality = data
          })
      )
    }

    if (internshipYear) {
      promiseStack.push(
        getContactPersonsSpecialityYear(internshipYear, user)
          .then(data => {
            contactPersonSpecialityYear = data
          })
      )
    }

    Promise.all(promiseStack).then(() => {
      const allContactPerson = contactPersonSchoolSpeciality.concat(contactPersonSpecialityYear)
      setContactPersons(allContactPerson)
      setLoadingContactPersons(false)
    })
  }

  const handleStateChange = newState => {
    onMaccsInternshipValidate({ ...maccsInternship, state: newState })
  }

  const lockMaccsInternshipConvention = document => {
    lockedMaccsInternshipDocuments(document, user)
      .then(json => {
        const newMaccsInternship = { ...maccsInternship }
        const newDocuments = documents.map(d => {
          if (d.id === document.id) {
            d.locked = true
          }

          return d
        })

        newMaccsInternship.documents = newDocuments
        setDocuments(newDocuments)
      })
  }

  const renderContactPersons = () => {
    if (loadingContactPersons) {
      return (
        <div className='flex-column'>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className='loading-icon black' />
            <div style={{ marginLeft: '4px', marginTop: '4px' }}>{t('Loading contact persons')}</div>
          </div>
        </div>
      )
    }

    if (!contactPersons.length) {
      return (
        <div className='flex-column'>
          {t('None')}
        </div>
      )
    }

    // TODO new contactPerson
    return (
      <div className='flex-column'>
        {contactPersons.length > 0 && contactPersons.map(contactPerson => <div key={contactPerson.email}>&nbsp;{`${contactPerson.lastname} ${contactPerson.firstname} - `}<a href={'mailto:' + contactPerson.email}>{contactPerson.email}</a>{contactPerson.phone !== null && contactPerson.phone !== '' ? ` - ${contactPerson.phone}` : ''}</div>)}
      </div>
    )
  }

  const renderSchedule = () => {
    if (!shifts.length > 0) {
      return (<div />)
    }

    const shiftsSort = shifts.sort((a, b) => {
      return moment(a.startDate).diff(moment(b.startDate))
    })
    const dates = []
    const dateIndex = moment(maccsInternship.startDate)
      .hours(0)
      .minutes(0)
      .seconds(0)
      .clone()
    const dateEnd = moment(maccsInternship.endDate)
      .hours(0)
      .minutes(0)
      .seconds(0)
      .clone()

    while (!dateIndex.isAfter(dateEnd)) {
      dates.push(dateIndex.clone())
      dateIndex.add(1, 'day')
    }

    return (
      <div className='internship-schedule'>
        <div className='v-spacing' />
        <Timeline>
          {dates.map(d => {
            const shift = shiftsSort.filter(s =>
              moment(s.startDate)
                .hours(0)
                .minutes(0)
                .seconds(0)
                .isSame(d)
            )[0]
            const day = d.format('DD/MM/YY')
            let icon = null
            let start = null
            let end = null
            let pause = null

            if (shift !== undefined) {
              if (
                moment(shift.startDate).format('DD/MM/YYYY') !==
                moment(shift.endDate).format('DD/MM/YYYY')
              ) {
                icon = <FontAwesomeIcon icon='moon' />
              }

              start = moment(shift.startDate).format('HH:mm')
              end = moment(shift.endDate).format('HH:mm')
              pause = moment()
                .hours(0)
                .minutes(0)
                .seconds(0)
                .add(shift.pause, 'minutes')
                .format('HH:mm')
            }
            return (
              <Timeline.Item
                color={
                  d.isoWeekday() === 6 || d.isoWeekday() === 7
                    ? '#E8E8E8'
                    : '#3C8DBC'
                }
                key={d.format('DD/MM/YY')}
              >
                {shift === undefined
                  ? (
                    <div className='flex-row'>
                      <b>{day}</b>&nbsp;:&nbsp;{t('No schedule.')}
                    </div>
                  )
                  : (
                    <div className='flex-row'>
                      <b>{day}</b>&nbsp;:&nbsp;de&nbsp;<b>{start}</b>&nbsp;à&nbsp;
                      <b>{end}</b>&nbsp;avec&nbsp;<b>{pause}</b>&nbsp;de
                      pause.&nbsp;{icon}
                    </div>
                  )}
              </Timeline.Item>
            )
          })}
        </Timeline>
      </div>
    )
  }

  const renderBody = () => {
    if (!loading && maccsInternship !== null) {
      const document = documents.length > 0
        ? documents
          .sort((d1, d2) =>
            moment(d1.updatedAt).isSameOrAfter(moment(d2.updatedAt))
              ? -1
              : 1
          )
          .filter(f => f.type === 'convention')[0]
        : undefined

      let maccsInternshipConvention = (
        <Button type='default' size='small' disabled>
          <FontAwesomeIcon icon='download' />
          &nbsp;{t('common.download')}
        </Button>
      )

      if (document !== undefined) {
        let lockButton = (
          <Tooltip
            placement='top'
            title={t(
              'This internship convention has been locked, the assistant cannot edit it anymore.'
            )}
          >
            <Button type='default' size='small' disabled>
              <FontAwesomeIcon icon='lock' />
            </Button>
          </Tooltip>
        )

        if (!document.locked) {
          lockButton = (
            <Popconfirm
              placement='top'
              title={t(
                'Accept and definitively lock this internship convention ?'
              )}
              okText={t('Accept and lock')}
              onConfirm={() => lockMaccsInternshipConvention(document)}
              cancelText={t('Cancel')}
            >
              <Tooltip
                placement='bottom'
                title={t(
                  'Accept and lock this internship convention to avoid the student to edit it.'
                )}
              >
                <Button type='default' size='small'>
                  <FontAwesomeIcon icon='lock-open' />
                </Button>
              </Tooltip>
            </Popconfirm>
          )
        }

        maccsInternshipConvention = (
          <div className='flex-row'>
            <Button
              type='default'
              size='small'
              onClick={() => downloadConvention(document, user)}
            >
              <FontAwesomeIcon icon='download' />
              &nbsp;{t('Download')}
            </Button>
            <div className='h-spacing' />
            {lockButton}
          </div>
        )
      }

      const maccsInternshipInfo = [
        {
          id: 1,
          icon: faClinicMedical,
          title: t('Institution'),
          caption:
            maccsInternship.institution === null
              ? t('Without institution')
              : maccsInternship.institution.name
        },
        {
          id: 2,
          icon: faPeopleArrows,
          title: t('Contact persons'),
          caption: renderContactPersons()
        },
        {
          id: 3,
          icon: faBriefcase,
          title: t('Working regime'),
          caption: maccsInternship.workingRegime === null
            ? t('Without working regime')
            : t('Working regime') + ' : ' + t(maccsInternship.workingRegimeName)
        },
        {
          id: 4,
          icon: 'calendar-alt',
          title: t('Dates'),
          caption:
            t('From') +
            ' ' +
            getTimezoneLessMoment(maccsInternship.startDate).format('DD MMMM YYYY') +
            ' ' +
            t('to') +
            ' ' +
            getTimezoneLessMoment(maccsInternship.endDate).format('DD MMMM YYYY')
        },
        {
          id: 5,
          icon: 'file',
          title: t('Internship convention'),
          caption: <div>{maccsInternshipConvention}</div>
        },
        {
          id: 6,
          icon: 'file',
          title: t('Other internship documents'),
          caption: (
            <div>
              <Button
                type='default'
                size='small'
                onClick={() => setMaccsInternshipFilesManagerOpen(true)}
              >
                <FontAwesomeIcon icon='paperclip' />
                &nbsp;{t('See')}
              </Button>
            </div>
          )
        },
        {
          id: 7,
          icon: 'calendar-alt',
          title: t('Schedule'),
          caption: renderSchedule()
        }
      ]

      return (
        <div className='flex-column'>
          <InformationDisplay information={maccsInternshipInfo} />
        </div>
      )
    } else {
      return (
        <LoadingSkeleton />
      )
    }
  }

  const title = useMemo(() => {
    const tooltipValues = {
      [InternshipStates.SCHEDULE_TO_VALIDATE]: t('Internship to validate, student uploaded signed document'),
      [InternshipStates.SCHEDULE_VALIDATED]: t('Internship validated')
    }

    return (
      <div className='internship-sheet-title'>
        <span>
          {t('Internship sheet')}
          {loading
            ? (
              <div className='loading-icon black' style={{ marginTop: '1px' }} />
            )
            : null}
        </span>
        {isObject(maccsInternship) && (
          <span className='internship-validation-button-custom'>
            <InternshipValidationButton
              validateButtonTooltip={t(tooltipValues[maccsInternship?.state] || 'Validate internship')}
              onDisplayValidationDrawer={() => setOpenValidationDrawer(true)}
              internship={maccsInternship}
            />
          </span>
        )}
      </div>
    )
  })

  return (
    <Drawer
      title={title}
      width='550px'
      onClose={onClose}
      visible={isObject(maccsInternship)}
    >
      {renderBody()}
      {openValidationDrawer &&
        <MaccsInternshipValidation
          maccsInternship={maccsInternship}
          onClose={() => setOpenValidationDrawer(false)}
          onValidate={() => handleStateChange(InternshipStates.SCHEDULE_VALIDATED)}
          onInvalidate={handleStateChange}
          bottom
        />}
      <FilesManager
        width='550px'
        entity={maccsInternshipFilesManagerOpen ? maccsInternship : null}
        entityName='MaccsInternship'
        onClose={() => setMaccsInternshipFilesManagerOpen(false)}
        fileType='document'
      />
    </Drawer>
  )
}

export default MaccsInternshipInfo
